import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../config/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Users, ArrowLeft, Search, Phone, Mail, Trash2, FileText
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_LABELS = {
  nouveau: { label: 'Nouveau', color: 'bg-blue-100 text-blue-700' },
  contacte: { label: 'Contacté', color: 'bg-yellow-100 text-yellow-700' },
  converti: { label: 'Converti', color: 'bg-green-100 text-green-700' },
  perdu: { label: 'Perdu', color: 'bg-red-100 text-red-700' }
};

const INSURANCE_LABELS = {
  'auto': 'Automobile',
  'habitation': 'Habitation',
  'sante': 'Santé',
  'prevoyance': 'Prévoyance',
  'rc_pro': 'RC Pro',
  'multirisque_pro': 'Multirisque Pro',
  'decennale': 'Décennale',
  'flotte': 'Flotte Auto',
  'autre': 'Autre'
};

export const AdminLeads = () => {
  const navigate = useNavigate();
  const { isAdmin, loading: authLoading } = useAuth();
  const [leads, setLeads] = useState([]);
  const [apporteurs, setApporteurs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterApporteur, setFilterApporteur] = useState('all');
  const [editingLead, setEditingLead] = useState(null);
  const [editData, setEditData] = useState({ status: '', notes: '' });

  useEffect(() => {
    if (authLoading) return;
    if (!isAdmin) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [isAdmin, authLoading, navigate]);

  const fetchData = async () => {
    try {
      const [leadsRes, apporteursRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/leads`),
        axios.get(`${API_URL}/api/admin/apporteurs`)
      ]);
      setLeads(leadsRes.data);
      setApporteurs(apporteursRes.data);
    } catch (err) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateLead = async () => {
    try {
      await axios.put(`${API_URL}/api/admin/leads/${editingLead.id}`, editData);
      toast.success('Lead mis à jour');
      setEditingLead(null);
      fetchData();
    } catch (err) {
      toast.error('Erreur');
    }
  };

  const handleDeleteLead = async (id) => {
    if (!window.confirm('Supprimer ce lead ?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/leads/${id}`);
      toast.success('Lead supprimé');
      fetchData();
    } catch (err) {
      toast.error('Erreur');
    }
  };

  const openEditModal = (lead) => {
    setEditingLead(lead);
    setEditData({ status: lead.status, notes: lead.notes || '' });
  };

  const filteredLeads = leads.filter(l => {
    const matchesSearch = l.client_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      l.client_phone?.includes(searchTerm);
    const matchesStatus = filterStatus === 'all' || l.status === filterStatus;
    const matchesApporteur = filterApporteur === 'all' || l.apporteur_id === filterApporteur;
    return matchesSearch && matchesStatus && matchesApporteur;
  });

  if (authLoading || loading) {
    return (
      <div className="min-h-screen pt-28 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Gestion Leads | Admin CAMBC</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="min-h-screen bg-slate-100 pt-20 pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <Link to="/admin" className="text-slate-500 hover:text-slate-700">
                <ArrowLeft size={24} />
              </Link>
              <h1 className="text-2xl font-bold text-slate-900">Leads Apporteurs</h1>
            </div>
            <div className="text-sm text-slate-500">
              {leads.length} lead(s) • {leads.filter(l => l.status === 'nouveau').length} nouveau(x)
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-4 mb-6">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <Input
                placeholder="Rechercher client..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="nouveau">Nouveau</SelectItem>
                <SelectItem value="contacte">Contacté</SelectItem>
                <SelectItem value="converti">Converti</SelectItem>
                <SelectItem value="perdu">Perdu</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterApporteur} onValueChange={setFilterApporteur}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Apporteur" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                {apporteurs.map(a => (
                  <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Edit Modal */}
          {editingLead && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <Card className="w-full max-w-md">
                <CardHeader>
                  <CardTitle>Modifier Lead</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="bg-slate-50 p-3 rounded-lg">
                    <p className="font-medium">{editingLead.client_name}</p>
                    <p className="text-sm text-slate-500">{editingLead.client_phone}</p>
                    <p className="text-sm text-slate-500">Apporteur: {editingLead.apporteur_name}</p>
                  </div>
                  <div>
                    <Label>Statut</Label>
                    <Select value={editData.status} onValueChange={(v) => setEditData({...editData, status: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="nouveau">Nouveau</SelectItem>
                        <SelectItem value="contacte">Contacté</SelectItem>
                        <SelectItem value="converti">Converti</SelectItem>
                        <SelectItem value="perdu">Perdu</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Notes</Label>
                    <Textarea
                      value={editData.notes}
                      onChange={(e) => setEditData({...editData, notes: e.target.value})}
                      rows={3}
                      placeholder="Ajouter des notes..."
                    />
                  </div>
                  <div className="flex gap-3">
                    <Button onClick={handleUpdateLead} className="flex-1">Enregistrer</Button>
                    <Button variant="outline" onClick={() => setEditingLead(null)}>Annuler</Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Leads List */}
          <Card className="bg-white shadow-lg border-0 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left p-4 text-sm font-semibold text-slate-600">Client</th>
                    <th className="text-left p-4 text-sm font-semibold text-slate-600">Contact</th>
                    <th className="text-left p-4 text-sm font-semibold text-slate-600">Type</th>
                    <th className="text-left p-4 text-sm font-semibold text-slate-600">Apporteur</th>
                    <th className="text-left p-4 text-sm font-semibold text-slate-600">Date</th>
                    <th className="text-left p-4 text-sm font-semibold text-slate-600">Statut</th>
                    <th className="text-right p-4 text-sm font-semibold text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredLeads.length > 0 ? (
                    filteredLeads.map((lead) => (
                      <tr key={lead.id} className="hover:bg-slate-50">
                        <td className="p-4">
                          <p className="font-medium text-slate-900">{lead.client_name}</p>
                          {lead.details && <p className="text-sm text-slate-500 truncate max-w-[200px]">{lead.details}</p>}
                        </td>
                        <td className="p-4">
                          <div className="flex flex-col gap-1">
                            <a href={`tel:${lead.client_phone}`} className="flex items-center gap-1 text-sm text-blue-600 hover:underline">
                              <Phone size={14} /> {lead.client_phone}
                            </a>
                            {lead.client_email && (
                              <a href={`mailto:${lead.client_email}`} className="flex items-center gap-1 text-sm text-slate-500 hover:underline">
                                <Mail size={14} /> {lead.client_email}
                              </a>
                            )}
                          </div>
                        </td>
                        <td className="p-4 text-slate-600">
                          {INSURANCE_LABELS[lead.insurance_type] || lead.insurance_type}
                        </td>
                        <td className="p-4 text-slate-600">{lead.apporteur_name}</td>
                        <td className="p-4 text-slate-500 text-sm">
                          {new Date(lead.created_at).toLocaleDateString('fr-FR')}
                        </td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${STATUS_LABELS[lead.status]?.color || 'bg-slate-100'}`}>
                            {STATUS_LABELS[lead.status]?.label || lead.status}
                          </span>
                        </td>
                        <td className="p-4 text-right">
                          <Button variant="ghost" size="sm" onClick={() => openEditModal(lead)}>
                            Modifier
                          </Button>
                          <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDeleteLead(lead.id)}>
                            <Trash2 size={16} />
                          </Button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} className="p-8 text-center text-slate-500">
                        <Users size={48} className="mx-auto mb-4 text-slate-300" />
                        <p>Aucun lead trouvé</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      </div>
    </>
  );
};
